import { useMemo } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardQuery, type DashboardFilters } from "@/hooks/useDashboardQuery";
import { buildDefectTileDefs, buildInspectionTileDefs } from "@/components/dashboard/roleStatCards";
import { getUserGreetingName } from "@/lib/profileUtils";

/**
 * Gemeinsame Daten-Verdrahtung der rollenspezifischen Dashboards (TES-1093):
 * user-scoped Dashboard-Stats (canSeeAll=false, neutrale Filter — die Fokus-
 * Sichten haben keine Filterleiste) + abgeleitete Status-Kachel-Definitionen.
 * Geteilt zwischen TesterDashboard und MangelbearbeiterDashboard.
 */

// Fokus-Sichten ohne Filterleiste — neutrale Filter für die user-scoped Stats.
const NEUTRAL_FILTERS: DashboardFilters = {
  inspectionStatusFilter: "all",
  defectStatusFilter: "all",
  severityFilter: "all",
  assignedToFilter: "all",
  teamId: null,
};

export function useRoleDashboardData(options: { includeUnassigned: boolean }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, profile, isAdmin, isQualitaetsmanager, isTester } = useAuth();
  const { includeUnassigned } = options;
  const userId = user?.id;

  const { stats, recentInspections, criticalDefects, isLoading } = useDashboardQuery(
    userId,
    false, // canSeeAll: Fokus-Sichten zeigen NUR die eigenen Daten
    NEUTRAL_FILTERS,
    { isAdmin, isQualitaetsmanager, isTester, language: i18n.language, t },
  );

  const userName = getUserGreetingName(profile, user?.email, t("common.user"));

  const inspectionTiles = useMemo(
    () => buildInspectionTileDefs({ stats, t, navigate, includeUnassigned }),
    [stats, t, navigate, includeUnassigned],
  );
  const defectTiles = useMemo(
    () => buildDefectTileDefs({ stats, t, navigate, includeUnassigned }),
    [stats, t, navigate, includeUnassigned],
  );

  return {
    userId,
    userName,
    stats,
    recentInspections,
    criticalDefects,
    /** Initial-Load ohne Daten → Skeleton zeigen. */
    showSkeleton: isLoading && !stats,
    inspectionTiles,
    defectTiles,
  };
}
